/**
 * Admin: Training earnings audit.
 *
 * Shows who earned the most PSS Coins from training, how long they have been
 * at it, the minimum hours of play their counters represent, and flags for
 * patterns that point at cheating:
 *  - count_exploit: more hands/scenarios credited than API calls ever made
 *    (the old client-supplied "count" hole, closed Sept 2026)
 *  - fast_earner: more than $15 per active day
 *  - scripted_speed: many hand calls arriving faster than a real hand can end
 *  - forged_calls: calls the app never makes (session score, correct answers
 *    without a scenario)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, HStack, VStack, Text, Button, Badge, Input, Spinner, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Tooltip, Switch, FormControl, FormLabel,
} from '@chakra-ui/react';
import { GraduationCap, RefreshCw } from 'lucide-react';
import gameApiService from '../../services/gameApiService';
import { POKER_COLORS } from '../../theme/designSystem';

interface Row {
  uid: string;
  username: string;
  earned: number;
  level: string;
  xp: number;
  hands: number;
  verifiedHands: number;
  scenarios: number;
  correct: number;
  badges: number;
  lifetimeCalls: number;
  firstActivityAt: number | null;
  lastPlayedAt: number | null;
  daysActive: number | null;
  minHoursNeeded: number;
  uncreditedCalls: number;
  flags: string[];
}

const FLAG_TEXT: Record<string, { label: string; color: string; tip: string }> = {
  count_exploit: { label: 'Count hole', color: 'red', tip: 'Credited far more hands/scenarios than calls ever made. Used the old forged-count hole.' },
  fast_earner: { label: 'Fast', color: 'orange', tip: 'Earned more than $15 per active day.' },
  scripted_speed: { label: 'Scripted', color: 'red', tip: 'Many hand reports arrived faster than a real hand can finish.' },
  forged_calls: { label: 'Forged', color: 'red', tip: 'Sent calls the app never makes.' },
};

const fmtDate = (t: number | null) => (t ? new Date(t).toLocaleDateString() : '—');
const money = (n: number) => `$${n.toFixed(2)}`;

const AdminTrainingEarnings: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [rules, setRules] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await gameApiService.trainingEarnings(150);
    if (r.success) {
      setRows((r.rows || []) as Row[]);
      setTotal(r.totalEarned || 0);
      setRules(r.rules || null);
    } else {
      setError(r.error || 'Failed to load');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) =>
      (!flaggedOnly || r.flags.length > 0) &&
      (!needle || r.username.toLowerCase().includes(needle) || r.uid.toLowerCase().includes(needle)));
  }, [rows, q, flaggedOnly]);

  const flaggedCount = rows.filter((r) => r.flags.length).length;

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <HStack>
          <GraduationCap size={20} color={POKER_COLORS.gold} />
          <Text fontSize="lg" fontWeight="bold" color="white">Training earnings</Text>
        </HStack>
        <Button size="sm" leftIcon={<RefreshCw size={14} />} onClick={load} isLoading={loading} variant="outline" colorScheme="cyan">
          Refresh
        </Button>
      </HStack>

      <HStack spacing={3} flexWrap="wrap">
        <Box bg="whiteAlpha.100" px={4} py={2} borderRadius="md">
          <Text fontSize="xs" color="gray.400">Paid to top {rows.length}</Text>
          <Text fontSize="lg" fontWeight="bold" color={POKER_COLORS.gold}>{money(total)}</Text>
        </Box>
        <Box bg="whiteAlpha.100" px={4} py={2} borderRadius="md">
          <Text fontSize="xs" color="gray.400">Flagged players</Text>
          <Text fontSize="lg" fontWeight="bold" color={flaggedCount ? 'red.300' : 'green.300'}>{flaggedCount}</Text>
        </Box>
        {rules && (
          <Box bg="whiteAlpha.100" px={4} py={2} borderRadius="md">
            <Text fontSize="xs" color="gray.400">Current limits</Text>
            <Text fontSize="sm" color="gray.200">
              {rules.pace?.handsPerHour} hands/hr · {rules.pace?.handsPerDay}/day · {rules.pace?.xpPerDay} XP/day · {rules.playthroughMultiplier}x playthrough
            </Text>
          </Box>
        )}
      </HStack>

      <HStack spacing={4} flexWrap="wrap">
        <Input size="sm" maxW="260px" placeholder="Search username or id" value={q} onChange={(e) => setQ(e.target.value)} bg="blackAlpha.400" color="white" />
        <FormControl display="flex" alignItems="center" w="auto">
          <FormLabel htmlFor="flagged-only" mb={0} fontSize="sm" color="gray.300">Flagged only</FormLabel>
          <Switch id="flagged-only" isChecked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} colorScheme="red" />
        </FormControl>
      </HStack>

      <Text fontSize="xs" color="gray.500">
        "Min hours" is the least time a real player needs to play the hands and scenarios on record (about 25s a hand, 30s a scenario).
        If Min hours is far above the hours between first and last activity, or the account is flagged, look closer before approving a redemption.
      </Text>

      {error && <Text color="red.300">{error}</Text>}
      {loading && rows.length === 0 ? (
        <HStack justify="center" py={8}><Spinner color="cyan.300" /></HStack>
      ) : (
        <TableContainer bg="blackAlpha.300" borderRadius="md">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th color="gray.400">Player</Th>
                <Th color="gray.400" isNumeric>Earned</Th>
                <Th color="gray.400">Level</Th>
                <Th color="gray.400" isNumeric>Hands</Th>
                <Th color="gray.400" isNumeric>Scenarios</Th>
                <Th color="gray.400" isNumeric>API calls</Th>
                <Th color="gray.400" isNumeric>Min hours</Th>
                <Th color="gray.400" isNumeric>Days</Th>
                <Th color="gray.400">First / last</Th>
                <Th color="gray.400">Flags</Th>
              </Tr>
            </Thead>
            <Tbody>
              {shown.map((r) => (
                <Tr key={r.uid} bg={r.flags.length ? 'rgba(255,60,60,0.08)' : undefined}>
                  <Td color="white">
                    <Text fontWeight="semibold">{r.username}</Text>
                    <Text fontSize="10px" color="gray.500">{r.uid}</Text>
                  </Td>
                  <Td isNumeric color={POKER_COLORS.gold} fontWeight="bold">{money(r.earned)}</Td>
                  <Td color="gray.200">{r.level}<Text fontSize="10px" color="gray.500">{r.xp.toLocaleString()} XP</Text></Td>
                  <Td isNumeric color="gray.200">{r.hands.toLocaleString()}</Td>
                  <Td isNumeric color="gray.200">{r.scenarios.toLocaleString()}<Text fontSize="10px" color="gray.500">{r.correct} correct</Text></Td>
                  <Td isNumeric color="gray.200">{r.lifetimeCalls.toLocaleString()}</Td>
                  <Td isNumeric color="gray.200">{r.minHoursNeeded}</Td>
                  <Td isNumeric color="gray.200">{r.daysActive ?? '—'}</Td>
                  <Td color="gray.300" fontSize="xs">{fmtDate(r.firstActivityAt)}<br />{fmtDate(r.lastPlayedAt)}</Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      {r.flags.length === 0 && <Badge colorScheme="green">OK</Badge>}
                      {r.flags.map((f) => (
                        <Tooltip key={f} label={FLAG_TEXT[f]?.tip || f}>
                          <Badge colorScheme={FLAG_TEXT[f]?.color || 'red'}>{FLAG_TEXT[f]?.label || f}</Badge>
                        </Tooltip>
                      ))}
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {shown.length === 0 && !loading && (
                <Tr><Td colSpan={10} color="gray.500" textAlign="center">No players match.</Td></Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </VStack>
  );
};

export default AdminTrainingEarnings;
